'use client';

import { useEffect, useMemo, useState } from 'react';

type NonProductionEnvironment = 'local' | 'dev' | 'stg' | 'test';

const ENVIRONMENT_LABELS: Record<NonProductionEnvironment, string> = {
  local: 'LOCAL',
  dev: 'DEV',
  stg: 'PREVIEW',
  test: 'TEST',
};

const ENVIRONMENT_STYLES: Record<NonProductionEnvironment, string> = {
  local: 'bg-environmentLocalBg text-environmentLocalFg',
  dev: 'bg-environmentDevBg text-environmentDevFg',
  stg: 'bg-environmentStgBg text-environmentStgFg',
  test: 'bg-environmentTestBg text-environmentTestFg',
};

const CONFIGURED_ENVIRONMENT = process.env.NEXT_PUBLIC_ENVIRONMENT;

const normalizeConfiguredEnvironment = (value: string | undefined): NonProductionEnvironment | null => {
  switch (value?.toLowerCase()) {
    case 'local':
      return 'local';
    case 'development':
    case 'dev':
      return 'dev';
    case 'staging':
    case 'stg':
      return 'stg';
    case 'testing':
    case 'test':
      return 'test';
    default:
      return null;
  }
};

export const resolveNonProductionEnvironment = ({
  configuredEnvironment,
  hostname,
}: {
  configuredEnvironment?: string;
  hostname?: string | null;
}): NonProductionEnvironment | null => {
  const normalizedHostname = hostname?.toLowerCase() ?? '';

  if (
    normalizedHostname === 'localhost' ||
    normalizedHostname === '127.0.0.1' ||
    normalizedHostname === '::1' ||
    normalizedHostname.endsWith('.local.sportsfi.tech')
  ) {
    return 'local';
  }

  if (normalizedHostname.endsWith('.stg.sportsfi.tech') || normalizedHostname === 'stg.sportsfi.tech') {
    return 'stg';
  }

  if (normalizedHostname.endsWith('.dev.sportsfi.tech') || normalizedHostname === 'dev.sportsfi.tech') {
    return 'dev';
  }

  return normalizeConfiguredEnvironment(configuredEnvironment);
};

export function NonProductionEnvironmentStrip() {
  const [hostname, setHostname] = useState<string | null>(null);

  useEffect(() => {
    setHostname(window.location.hostname);
  }, []);

  const environment = useMemo(
    () =>
      resolveNonProductionEnvironment({
        configuredEnvironment: CONFIGURED_ENVIRONMENT,
        hostname,
      }),
    [hostname]
  );

  if (environment == null) {
    return null;
  }

  return (
    <div
      aria-label={`${ENVIRONMENT_LABELS[environment]} environment`}
      data-testid="non-production-environment-strip"
      role="note"
    >
      <div
        aria-hidden="true"
        className={`pointer-events-none fixed inset-x-0 top-0 z-[2147483647] h-[4px] ${ENVIRONMENT_STYLES[environment]}`}
      />
      <div className="pointer-events-none fixed right-0 top-0 z-[2147483647]">
        <div
          className={`rounded-bl-[6px] px-[10px] py-[3px] text-[11px] font-bold uppercase leading-[16px] tracking-[0.16em] shadow-sm ${ENVIRONMENT_STYLES[environment]}`}
        >
          {ENVIRONMENT_LABELS[environment]}
        </div>
      </div>
    </div>
  );
}
